import _ from 'lodash';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import GenericAttachment from './generic_attachment';
import { InlineAttachmentWrapper, PlaceholderInlineImage } from './inline_image_attachment';
import VideoPlayIcon from 'components/lib/icons/video_play_icon';

export default function InlineVideoAttachment({ attachment, inlinePreviewVideoUrl, onClick, onLoad }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleClick = isLoaded ? onClick : _.noop;

  function handleLoadedMetadata() {
    setIsLoaded(true);
    onLoad && onLoad();
  }

  // A 404 or a codec the browser can't decode never fires onLoadedMetadata, which would otherwise leave the
  // preview stuck on the placeholder forever. Fall back to the generic attachment card (which opens the modal
  // on click) and re-measure the row height, mirroring the modal's "No preview available" fallback.
  function handleError() {
    setHasError(true);
    onLoad && onLoad();
  }

  if (hasError) {
    return <GenericAttachment attachment={attachment} onClick={onClick} />;
  }

  // preload="metadata" guarantees dimensions but not a painted frame, so some browsers (Chrome
  // especially) show a black rectangle. Seeking just past the start with the #t=0.1 media fragment
  // forces a real frame to decode as the poster. The modal plays from the un-fragmented URL.
  const posterUrl = inlinePreviewVideoUrl ? `${inlinePreviewVideoUrl}#t=0.1` : undefined;

  return (
    <InlineAttachmentWrapper attachment={attachment} onClick={handleClick}>
      {isLoaded ? null : <PlaceholderInlineImage />}
      <VideoContainer style={isLoaded ? undefined : { display: 'none' }}>
        <Video
          className="inlineVideoAttachment-video"
          muted
          onError={handleError}
          onLoadedMetadata={handleLoadedMetadata}
          preload="metadata"
          src={posterUrl}
        />
        <PlayOverlay>
          <VideoPlayIcon height={48} width={48} />
        </PlayOverlay>
      </VideoContainer>
    </InlineAttachmentWrapper>
  );
}

InlineVideoAttachment.propTypes = {
  attachment: PropTypes.object,
  inlinePreviewVideoUrl: PropTypes.string,
  onClick: PropTypes.func,
  onLoad: PropTypes.func,
};

const VideoContainer = styled.div`
  cursor: pointer;
  display: inline-block;
  margin-bottom: 1px;
  max-width: 400px;
  position: relative;
  width: 100%;
`;

const Video = styled.video`
  display: block;
  height: auto;
  max-width: 400px;
  width: 100%;
`;

const PlayOverlay = styled.div`
  align-items: center;
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
`;
